import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState, useEffect } from 'react';
import { Button, Space, Tooltip, Upload, Message, Table } from '@bedrock/components';
import { Upload as UploadIcon } from '@bedrock/icons-react'
import { useControllableValue, useMemoizedFn, useRequest, useDebounceEffect } from 'ahooks';
import TableForm, { FormColumn } from '#components/TableForm';
import { Delete } from '@bedrock/icons-react';
import { BrFile, UploadChangeParam } from '@bedrock/components/lib/Upload/types';
import { SFormElement } from '#components/SForm';
import { SFormItemProps } from '#components/SForm/SFormItem';
import LoadingBtn from './LoadingBtn';
import { InvoiceDetailVo, outsourceCommonFilterOptions, commonFilterOptions } from '#services/api';
import { $sType } from '#utils';
import _ from 'lodash';
import styles from './index.less';

interface Props {
    value?: any[];
    onChange?: (value: any[]) => void;
    view?: boolean;
    id?: string;
    content?: {
        belongArea?: number;
        isOverseaInvoice?: boolean;
        currency?: string;
        demandId?: number;
    }
}

enum enumInvoiceType {
    'CHINA' = 'CHINA',
    'FOREIGN' = 'FOREIGN',
    'NORMAL' = 'NORMAL'
}

const requestCommonFilterOptionParams = {
    keys: ['invoice_type']
}

const cellConfig = {
    onCell: () => {
        return {
            style: {
                verticalAlign: 'middle'
            }
        }
    }
}

const getInvoiceType = (isOverseaInvoice, belongArea) => {
    if (typeof isOverseaInvoice !== 'boolean' || !belongArea) {
        return enumInvoiceType.NORMAL
    }
    if (isOverseaInvoice) {
        return enumInvoiceType.FOREIGN
    } else {
        return enumInvoiceType.CHINA
    }
}

const InvoiceInformation = forwardRef((props: Props, ref) => {
    const { id, content, view } = props;
    const { belongArea, isOverseaInvoice, currency, demandId } = content || {};
    const invoiceTypeStatus = getInvoiceType(isOverseaInvoice, belongArea);
    const tableFormRef = useRef(null);
    const [state, setState] = useControllableValue(props);
    const [localState, setLocalState] = useState(state);
    const [uploading, setUploading] = useState(false);
    // 可上传的文件列表
    const [canUploadList, setCanUploadList] = useState([]);
    // 取消上传的文件列表
    const [cancelUploadList, setCancelUploadList] = useState([]);
    const [lastUploadParams, setLastUploadParams] = useState({
        belongArea,
        isOverseaInvoice
    });

    const stateToTable = useMemoizedFn((arr) => {
        return arr?.map((v) => {
            return {
                ...v,
                amount_with_tax: {
                    value: v.amount_with_tax,
                    currency,
                },
                amount_without_tax: {
                    value: v.amount_without_tax,
                    currency
                },
                tax: {
                    value: v.tax,
                    currency,
                },
                blob_id: v.attachments?.map(ele => ({
                    id: ele.id,
                    name: ele.blob?.filename,
                    size: ele.blob?.byte_size,
                    uid: ele.blob?.key,
                    blob: ele.blob,
                }))
            }
        });
    });
    const [stateTable, setStateTable] = useState<any[]>(stateToTable(state));

    const tableToState = useMemoizedFn((arr) => {
        return arr?.map((v, i) => {
            return {
                ...v,
                blob_id: v.attachments?.[0]?.id,
                amount_with_tax: v.amount_with_tax?.value || 0,
                amount_without_tax: v.amount_without_tax?.value || 0,
                tax: v.tax?.value || 0,
            }
        });
    });

    // 内外部数据模型对齐
    useEffect(() => {
        if (!_.isEqual(state, localState)) {
            setLocalState(state);
            setStateTable(stateToTable(state));
        } else if (!_.isEqual(localState, tableToState(stateTable))) {
            const localTable = tableToState(stateTable);
            setLocalState(localTable);
            setState(localTable);
        }
    }, [state, localState, stateTable]);


    const { data: filterOptions } = useRequest(() =>
        $sType === 'inner' ? commonFilterOptions(requestCommonFilterOptionParams) : outsourceCommonFilterOptions(requestCommonFilterOptionParams)
        , {
            ready: !!stateTable?.length
        }
    )

    const tableTotalPrice = useMemo(() => {
        const initPrice = {
            value: 0,
            currency,
        }
        const total = stateTable?.reduce((pre, cur) => {
            return {
                amount_with_tax: {
                    value: pre.amount_with_tax.value + Number(cur.amount_with_tax.value),
                    currency
                },
                amount_without_tax: {
                    value: pre.amount_without_tax.value + Number(cur.amount_without_tax.value),
                    currency
                },
                tax: {
                    value: pre.tax.value + Number(cur.tax.value),
                    currency
                }
            }
        },
            {
                amount_with_tax: initPrice,
                amount_without_tax: initPrice,
                tax: initPrice
            });

        return total || {}
    }, [stateTable]);

    useDebounceEffect(
        () => {
            let flag = false;
            const newTableState = stateTable?.map((v) => {
                const amount_without_tax = v.amount_with_tax.value - v.tax.value
                if (amount_without_tax !== v.amount_without_tax.value) {
                    flag = true
                }
                return {
                    ...v,
                    amount_without_tax: {
                        value: amount_without_tax,
                        currency
                    }
                }
            })
            if (flag) {
                setStateTable(newTableState);
            }
        },
        [stateTable],
        {
            wait: 500,
            leading: false,
            trailing: true,
        },
    );

    const add = (item) => {
        const newState = [...(stateTable || []), item]
        setStateTable(newState);
    }

    const remove = (index: number) => {
        const newState = stateTable.filter((v, i: number) => i !== index)
        setStateTable(newState);
    }

    const columns: FormColumn[] = [
        {
            title: '#',
            dataIndex: 'index',
            width: 33,
            align: 'left',
            fixed: true,
            ...cellConfig,
            render: (text, record, index) => (< span style={{ fontWeight: 'bold' }}> {index + 1}</span >)
        },
        {
            title: '发票附件',
            dataIndex: 'blob_id',
            align: 'left',
            width: 260,
            required: !view,
            fixed: true,
            ...cellConfig,
            formItem: () => {
                return {
                    componentClass: 'upload',
                    view: true,
                    elementProps: {
                        style: {
                            width: 260
                        }
                    }
                } as SFormItemProps
            }
        },
        {
            title: '发票类型',
            dataIndex: 'invoice_type',
            width: 200,
            align: 'left',
            ...cellConfig,
            render: (value) => {
                if (!value) {
                    return '--'
                }
                const options = filterOptions?.data?.find(v => v.key === 'invoice_type')?.options;
                const text = options?.find(v => v.value === value)?.name || '--';
                const showTip = ['10100', '10101'].includes(value);
                return (
                    <>
                        {text}
                        <br />
                        {showTip && (
                            <span style={{ color: '#E08216' }}>
                                纸质发票，需寄送纸质原件
                            </span>
                        )}
                    </>
                )
            }
        },
        {
            title: '发票号码',
            dataIndex: 'invoice_no',
            width: 160,
            align: 'left',
            required: invoiceTypeStatus === enumInvoiceType.FOREIGN,
            ...cellConfig,
            formItem: () => {
                return {
                    formItemKey: 'invoice_no',
                    componentClass: 'input',
                    view: view || invoiceTypeStatus === enumInvoiceType.CHINA,
                    required: true,
                    elementProps: {
                        ableCopy: true,
                    },
                } as SFormItemProps
            },
        },
        {
            title: '发票代码',
            dataIndex: 'invoice_code',
            width: 200,
            align: 'left',
            ...cellConfig,
            formItem: () => {
                return {
                    formItemKey: 'invoice_code',
                    componentClass: 'input',
                    view: true,
                    required: true,
                    elementProps: {
                        ableCopy: true,
                    },
                } as SFormItemProps
            },
        },
        {
            title: '含税总金额',
            dataIndex: 'amount_with_tax',
            width: 260,
            align: 'left',
            required: invoiceTypeStatus === enumInvoiceType.FOREIGN,
            ...cellConfig,
            formItem: () => {
                return {
                    view: view || invoiceTypeStatus === enumInvoiceType.CHINA,
                    required: invoiceTypeStatus === enumInvoiceType.FOREIGN,
                    componentClass: 'currencyType',
                    elementProps: {
                        selectDisabled: true,
                        currencyColor: 'black',
                        min: 0,
                    },
                } as SFormItemProps;
            },
        },
        {
            title: '税额',
            dataIndex: 'tax',
            width: 260,
            align: 'left',
            required: invoiceTypeStatus === enumInvoiceType.FOREIGN,
            ...cellConfig,
            formItem: () => {
                return {
                    view: view || invoiceTypeStatus === enumInvoiceType.CHINA,
                    required: invoiceTypeStatus === enumInvoiceType.FOREIGN,
                    componentClass: 'currencyType',
                    elementProps: {
                        selectDisabled: true,
                        currencyColor: 'black',
                        min: 0,
                    },
                } as SFormItemProps;
            },
        },
        {
            title: '不含税金额',
            dataIndex: 'amount_without_tax',
            width: 200,
            align: 'left',
            ...cellConfig,
            formItem: () => {
                return {
                    view: true,
                    componentClass: 'currencyType',
                    elementProps: {
                        currencyColor: 'black',
                        min: 0,
                    },
                } as SFormItemProps
            },
        },
        !view && {
            title: '操作',
            dataIndex: 'action',
            align: 'left',
            fixed: 'right',
            ...cellConfig,
            render: (text, record, index) => {
                return <Button
                    type="text-subtle"
                    icon={<Delete />}
                    onClick={() => remove(index)}
                    danger
                />
            },
        },
    ];

    useImperativeHandle(ref, () => ({
        validateFields: () => {
            if (uploading) {
                return Promise.reject('当前文件正在上传中，请稍后再试')
            }
            if (!tableFormRef.current?.form?.validateFields) {
                return Promise.resolve()
            }
            return tableFormRef.current?.form?.validateFields()
                .catch((error) => {
                    const errMessage = error?.errorFields?.[0]?.errors?.[0]
                    return Promise.reject(errMessage)
                })
        },
    }));

    const beforeUpload = (file: BrFile, e) => {
        const fileType = file.name.substring(file.name.lastIndexOf('.') + 1).toLocaleLowerCase();
        if (!['jpg', 'jpeg', 'png', 'ofd', 'pdf'].includes(fileType)) {
            Message.error('格式不符合要求');
            return false
        }

        if (fileType !== 'pdf' && file.size > 8 * 1024 * 1024) {
            Message.error('除pdf外最大支持8M');
            return false
        }

        if (fileType === 'pdf' && file.size > 50 * 1024 * 1024) {
            Message.error('pdf最大支持50M');
            return false
        }

        return true
    }

    useEffect(() => {
        if (uploading) {
            // 发票地区/是否海外参数修改时，忽略上传中的文件
            if (belongArea !== lastUploadParams.belongArea || isOverseaInvoice !== lastUploadParams.isOverseaInvoice) {
                setUploading(false);
                setCanUploadList([]);
                setCancelUploadList((arr) => [...arr, ...canUploadList]);
            }
        }
    }, [belongArea, isOverseaInvoice, uploading, lastUploadParams])

    const onUploadChange = ({ file }: UploadChangeParam) => {
        const { status, response } = file;
        if (status === 'uploading') {
            if (cancelUploadList.find(v => v.uid === file.uid)) {
                return
            }
            if (!canUploadList.find(v => v.uid === file.uid)) {
                const startUploadTime = +Date.now();
                setCanUploadList((arr) =>
                    [...arr,
                    {
                        belongArea,
                        isOverseaInvoice,
                        uid: file.uid,
                        startUploadTime,
                    }
                    ]);
                setLastUploadParams({
                    belongArea,
                    isOverseaInvoice,
                })
                setUploading(true)
            }
        }
        if (status === 'done' && response?.code === 1) {
            const uploadItem = canUploadList.find(v => v.uid === file.uid)
            if (!uploadItem) {
                return
            }
            setUploading(false);
            const data = response?.data?.invoice_detail as InvoiceDetailVo || {};
            const item = {
                ...data,
                blob_id: data.attachments?.map(ele => ({
                    id: ele.id,
                    name: ele.blob?.filename,
                    size: ele.blob?.byte_size,
                    uid: ele.blob?.key,
                    blob: ele.blob,
                })),
                amount_with_tax: {
                    value: data.amount_with_tax ?? '',
                    currency,
                },
                amount_without_tax: {
                    value: data.amount_without_tax ?? '',
                    currency
                },
                tax: {
                    value: data.tax ?? '',
                    currency,
                },
            }
            add(item)
            // const endUploadTime = +Date.now();
            const isOtherArea = belongArea === 99;
            if (
                // (endUploadTime - uploadItem.startUploadTime) > 20 * 1000
                !data.invoice_type
                && !data.invoice_no
                && !data.amount_with_tax
                && !data.tax
                && !isOtherArea
                && invoiceTypeStatus === enumInvoiceType.FOREIGN
            ) {
                Message.warn({
                    content: '识别失败，请手工填写',
                });
            } else {
                Message.success({
                    content: (invoiceTypeStatus === enumInvoiceType.FOREIGN && isOtherArea) ? '上传成功' : '上传并识别成功',
                });
            }
        }
        if (status === 'done' && response?.code !== 1) {
            if (!canUploadList.find(v => v.uid === file.uid)) {
                return
            }
            setUploading(false);
            Message.error({
                content: response?.message || '上传失败',
            });
        }
    }

    return (
        <div className={styles['invoice-information']} id={id}>
            <Space split={<span className={styles['invoice-information-line']}>|</span>} align='start' className={styles['invoice-information-header']}>
                <Space size='xxs' align='start'>
                    <span>含税总金额合计：</span>
                    <SFormElement
                        componentClass='currencyType'
                        value={{
                            value: tableTotalPrice.amount_with_tax?.value,
                            currency,
                        }}
                        view
                        currencyColor='black'
                    />
                </Space>
                <Space size='xxs' align='start'>
                    <span>不含税金额合计：</span>
                    <SFormElement
                        componentClass='currencyType'
                        value={{
                            value: tableTotalPrice.amount_without_tax?.value,
                            currency,
                        }}
                        view
                        currencyColor='black'
                    />
                </Space>
            </Space>
            <TableForm
                ref={tableFormRef}
                value={stateTable}
                scroll={{ x: 'max-content' }}
                emptyText="暂无数据"
                onChange={setStateTable}
                formColumns={columns}
                summary={() => {
                    return !view && invoiceTypeStatus !== enumInvoiceType.NORMAL && (
                        <Table.Summary>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={2}>
                                    <Space>
                                        <Tooltip title='单次仅允许上传1个，支持jpg，jpeg，png，ofd及pdf；除pdf外最大支持8M，pdf单页大小不能超过8M'>
                                            <div style={{ width: 100 }}>
                                                <Upload
                                                    withCredentials
                                                    action="/api/outsource/v2/java_proxy/demand-invoice/memory-upload"
                                                    showFileList={false}
                                                    beforeUpload={beforeUpload}
                                                    onChange={onUploadChange}
                                                    data={{
                                                        belongArea,
                                                        isOverseaInvoice,
                                                        demandId,
                                                    }}
                                                    trigger={
                                                        <Button
                                                            disabled={uploading}
                                                            size="medium"
                                                            type="checked"
                                                            icon={<UploadIcon translate={undefined} onAuxClick={undefined}
                                                                onAuxClickCapture={undefined} />}>
                                                            上传发票
                                                        </Button>
                                                    }
                                                    type='button'
                                                >
                                                </Upload>
                                            </div>
                                        </Tooltip>
                                        {uploading && <LoadingBtn />}
                                    </Space>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )
                }}
            />
        </div>
    )
})

export default InvoiceInformation;
